import { Component, OnInit } from '@angular/core';
import { Catalogo } from '@interfaces/interface-bakend/shared/catalogo/catalogo.interface';
import { SignoCentroMedico } from '@interfaces/interface-bakend/entidades/signo-centro-medico.interface';
import { SignoSedeSearch } from '@interfaces/interface-bakend/cartilla/cartilla.interface';
import { SignoPlan } from '@interfaces/interface-bakend/entidades/signo-plan.interface';
import { SignoLocalidad } from '@interfaces/interface-bakend/entidades/signo-localidad.interface';
import { User } from '@interfaces/interface-bakend/seguridad/users/user.interface';
import { AuthService } from '@services/auth/auth.service';
import { CatalogoService } from '@services/shared/catalogo.service';
import { CartillaService } from '@services/cartilla/cartilla.service';
import { SignoPlanService } from '@services/entidades/signo-planes.service';
import { buscarPlanPorGecros } from '@shared/utils/plan.util';
import { SignoLocalidadService } from '@services/entidades/signo-localidades.service';
import { EntidadesKeys } from '@enum/entidades-keys.enum';
import { MatDialog } from '@angular/material/dialog';
import { CentrosCardComponent } from './centros-card/centros-card.component';

interface PhoneNumberInfo {
  number: string;
  type: 'phone' | 'whatsapp';
}

@Component({
  selector: 'app-cartilla-buscador',
  templateUrl: './cartilla-buscador.component.html',
  styleUrls: ['./cartilla-buscador.component.scss']
})
export class CartillaBuscadorComponent implements OnInit {
  rubros: Catalogo[] = [];
  especialidades: Catalogo[] = [];
  planes: SignoPlan[] = [];
  zonas: Catalogo[] = [];
  localidades: SignoLocalidad[] = [];
  usuario!: User;

  flagCargando = true;
  flagBuscando = false;

  selectedRubro: Catalogo | null = null;
  selectedEspecialidad: Catalogo | null = null;
  mostrandoEspecialidades = false;

  resultadosCentrosCercanos: SignoCentroMedico[] = [];
  resultadosCentrosLejanos: SignoCentroMedico[] = [];
  private centrosCercanosSinFiltro: SignoCentroMedico[] = [];
  private centrosLejanosSinFiltro: SignoCentroMedico[] = [];
  private distanciaCache: Map<SignoCentroMedico, number> = new Map();
  private readonly DISTANCIA_CERCANA = 10;
  private readonly DISTANCIA_LEJANA = 30;
  private readonly MAX_LEJANOS = 10;
  sinResultados = false;
  sinResultadosCercania = false;
  mostrandoCentroMasLejano = false;
  mostrandoFallbackManual = false;
  mensajeFallbackManual = '';
  gruposManuales: { titulo: string; centros: SignoCentroMedico[] }[] = [];

  userLocation: { latitude: number; longitude: number } = { latitude: 0, longitude: 0 };
  locationReady = false;
  mostrarBotonCercania = false;
  geoEstado: 'cargando' | 'ok' | 'denegada' = 'cargando';

  showPhoneModal = false;
  phoneNumbersForModal: PhoneNumberInfo[] = [];
  centerNameForModal = '';

  planDefaultId: string | null = null;
  planDefaultNombre = '';
  planId: string | null = null;
  planNombre = '';
  usandoPlanPropio = true;
  planDropdownOpen = false;

  modoManualUbicacion = false;
  zonaSeleccionada: string | null = null;
  localidadSeleccionada: string | null = null;
  zonaDropdownOpen = false;
  localidadDropdownOpen = false;

  filtroNombre = '';
  filtroEspecialidad = '';

  private allLocalidades: SignoLocalidad[] = [];

  private iconMap: Record<string, string> = {
    'CUERPO MÉDICO': 'groups',
    'DIAGNÓSTICO': 'analytics',
    'FARMACIAS': 'medical_services',
    'INTERNACION': 'bed',
    'ODONTOLOGÍA': 'mood',
    'OPTICA': 'visibility',
    'OPTICAS': 'visibility',
    'SALUD MENTAL': 'favorite',
    'URGENCIAS': 'monitor_heart',
  };

  constructor(
    private authService: AuthService,
    private catalogoService: CatalogoService,
    private cartillaService: CartillaService,
    private signoPlanService: SignoPlanService,
    private signoLocalidadService: SignoLocalidadService,
    private dialog: MatDialog
  ) {}

  get totalCentrosMostrados(): number {
    return this.resultadosCentrosCercanos.length + this.resultadosCentrosLejanos.length;
  }

  get totalCentrosSinFiltro(): number {
    return this.centrosCercanosSinFiltro.length + this.centrosLejanosSinFiltro.length;
  }

  async ngOnInit(): Promise<void> {
    this.usuario = await this.authService.getUserLogon();
    await Promise.all([
      this.getRubros(),
      this.getEspecialidades(),
      this.getPlanes(),
      this.getZonas(),
      this.getLocalidades(),
      this.getGeo(),
    ]);
    this.setPlanFromUser();
    this.flagCargando = false;
  }

  private setPlanFromUser(): void {
    const planDefault = this.usuario?.signoGecrosAfiliado?.plan;
    if (planDefault) {
      const planObj = buscarPlanPorGecros(planDefault, this.planes) as SignoPlan;
      if (planObj?._id) {
        this.planDefaultId = planObj._id;
        this.planDefaultNombre = planObj.denominacion || planDefault;
        this.planId = planObj._id;
        this.planNombre = planObj.denominacion || planDefault;
      }
    }
  }

  getUserDisplayName(): string {
    return this.usuario.fullName || this.usuario.nombrePersona || 'Usuario';
  }

  async getRubros(): Promise<void> {
    const rubros = await this.catalogoService.findDescendantsByKey(EntidadesKeys.RUBROS);
    this.rubros = rubros.filter(r => r.logico_1 !== false);
  }

  async getEspecialidades(): Promise<void> {
    const especialidades = await this.catalogoService.findDescendantsByKey(EntidadesKeys.ESPECIALIDADES);
    this.especialidades = especialidades.filter(e => e.logico_1 !== false);
  }

  async getPlanes(): Promise<void> {
    this.planes = await this.signoPlanService.getPlanes();
  }

  async getZonas(): Promise<void> {
    this.zonas = await this.catalogoService.findDescendantsByKey(EntidadesKeys.ZONAS);
  }

  async getLocalidades(): Promise<void> {
    this.allLocalidades = await this.signoLocalidadService.getLocalidades();
    this.localidades = [...this.allLocalidades];
  }

  getGeo(): void {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(position => {
        this.userLocation.latitude = position.coords.latitude;
        this.userLocation.longitude = position.coords.longitude;
        this.mostrarBotonCercania = true;
        this.geoEstado = 'ok';
      }, error => {
        console.error('Error al obtener la ubicación:', error);
        this.geoEstado = 'denegada';
        this.mostrarBotonCercania = false;
        this.modoManualUbicacion = true;
      });
    } else {
      console.error('Geolocalización no es compatible con este navegador.');
      this.geoEstado = 'denegada';
      this.mostrarBotonCercania = false;
      this.modoManualUbicacion = true;
    }
    this.locationReady = true;
  }

  getIconForRubro(rubro: Catalogo): string {
    const concepto = rubro.concepto?.toUpperCase() || '';
    return this.iconMap[concepto] || 'circle';
  }

  getEspecialidadesFiltradas(): Catalogo[] {
    if (!this.filtroEspecialidad.trim()) return this.especialidades;
    const f = this.normalizarTexto(this.filtroEspecialidad);
    return this.especialidades.filter(e =>
      this.normalizarTexto(e.concepto || '').includes(f)
    );
  }

  private reBuscarSiActivo(): void {
    if (this.selectedRubro) {
      this.ejecutarBusqueda({ rubro: this.selectedRubro._id });
    } else if (this.selectedEspecialidad) {
      this.ejecutarBusqueda({ especialidad: this.selectedEspecialidad._id });
    }
  }

  onEspecialidadesMedicas(): void {
    this.selectedRubro = null;
    this.selectedEspecialidad = null;
    this.resultadosCentrosCercanos = [];
    this.resultadosCentrosLejanos = [];
    this.centrosCercanosSinFiltro = [];
    this.centrosLejanosSinFiltro = [];
    this.distanciaCache = new Map();
    this.sinResultados = false;
    this.sinResultadosCercania = false;
    this.filtroNombre = '';
    this.filtroEspecialidad = '';
    this.mostrandoEspecialidades = true;
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  volverARubros(): void {
    this.mostrandoEspecialidades = false;
    this.selectedRubro = null;
    this.selectedEspecialidad = null;
    this.resultadosCentrosCercanos = [];
    this.resultadosCentrosLejanos = [];
    this.centrosCercanosSinFiltro = [];
    this.centrosLejanosSinFiltro = [];
    this.distanciaCache = new Map();
    this.sinResultados = false;
    this.sinResultadosCercania = false;
    this.filtroNombre = '';
    this.filtroEspecialidad = '';
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  volverDesdeResultados(): void {
    this.resultadosCentrosCercanos = [];
    this.resultadosCentrosLejanos = [];
    this.centrosCercanosSinFiltro = [];
    this.centrosLejanosSinFiltro = [];
    this.distanciaCache = new Map();
    this.sinResultados = false;
    this.sinResultadosCercania = false;
    this.filtroNombre = '';
    if (this.selectedEspecialidad) {
      this.selectedEspecialidad = null;
    }
    if (this.selectedRubro) {
      this.selectedRubro = null;
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  togglePlanDropdown(): void {
    this.planDropdownOpen = !this.planDropdownOpen;
  }

  selectPlan(plan: SignoPlan): void {
    this.planId = plan._id || null;
    this.planNombre = plan.denominacion || '';
    this.usandoPlanPropio = false;
    this.planDropdownOpen = false;
    this.reBuscarSiActivo();
  }

  usarMiPlan(): void {
    this.planId = this.planDefaultId;
    this.planNombre = this.planDefaultNombre;
    this.usandoPlanPropio = true;
    this.planDropdownOpen = false;
    this.reBuscarSiActivo();
  }

  toggleModoUbicacion(): void {
    if (this.modoManualUbicacion && this.geoEstado !== 'ok') {
      return;
    }
    this.modoManualUbicacion = !this.modoManualUbicacion;
    if (!this.modoManualUbicacion) {
      this.zonaSeleccionada = null;
      this.localidadSeleccionada = null;
      this.localidades = [...this.allLocalidades];
      this.reBuscarSiActivo();
    }
  }

  async selectZona(zona: Catalogo | null): Promise<void> {
    this.zonaSeleccionada = zona ? (zona._id as string) : null;
    this.zonaDropdownOpen = false;
    this.localidadSeleccionada = null;

    if (zona?._id) {
      try {
        this.localidades = await this.signoLocalidadService.listarLocalidadesByZona(zona._id);
      } catch (error) {
        console.error('Error al obtener localidades por zona:', error);
        this.localidades = [];
      }
    } else {
      this.localidades = [...this.allLocalidades];
    }
    this.reBuscarSiActivo();
  }

  selectLocalidad(localidad: SignoLocalidad | null): void {
    this.localidadSeleccionada = localidad ? (localidad._id as string) : null;
    this.localidadDropdownOpen = false;
    this.reBuscarSiActivo();
  }

  toggleZonaDropdown(): void {
    this.zonaDropdownOpen = !this.zonaDropdownOpen;
    this.localidadDropdownOpen = false;
  }

  toggleLocalidadDropdown(): void {
    this.localidadDropdownOpen = !this.localidadDropdownOpen;
    this.zonaDropdownOpen = false;
  }

  getSelectedZonaText(): string {
    if (!this.zonaSeleccionada) return '';
    const zona = this.zonas.find(z => z._id === this.zonaSeleccionada);
    return zona?.concepto || '';
  }

  getSelectedLocalidadText(): string {
    if (!this.localidadSeleccionada) return '';
    const loc = this.localidades.find(l => l._id === this.localidadSeleccionada);
    return loc?.nombre || '';
  }

  normalizarConcepto(texto: string | undefined): string {
    if (!texto) return '';
    const menores = new Set(['de', 'y', 'la', 'el', 'los', 'las', 'en', 'por', 'para', 'del', 'a', 'o', 'u', 'con', 'sin', 'al']);
    return texto
      .toLowerCase()
      .trim()
      .split(/\s+/)
      .map((p, i) => {
        if (p && menores.has(p) && i !== 0) return p;
        return p.charAt(0).toUpperCase() + p.slice(1);
      })
      .join(' ');
  }

  async onRubroTap(rubro: Catalogo): Promise<void> {
    this.selectedRubro = rubro;
    this.selectedEspecialidad = null;
    this.mostrandoEspecialidades = false;
    await this.ejecutarBusqueda({ rubro: rubro._id });
  }

  async onEspecialidadTap(especialidad: Catalogo): Promise<void> {
    this.selectedEspecialidad = especialidad;
    this.selectedRubro = null;
    this.mostrandoEspecialidades = false;
    await this.ejecutarBusqueda({ especialidad: especialidad._id });
  }

  private async ejecutarBusqueda(data: { rubro?: string; especialidad?: string }): Promise<void> {
    this.resultadosCentrosCercanos = [];
    this.resultadosCentrosLejanos = [];
    this.centrosCercanosSinFiltro = [];
    this.centrosLejanosSinFiltro = [];
    this.distanciaCache = new Map();
    this.sinResultados = false;
    this.sinResultadosCercania = false;
    this.filtroNombre = '';
    this.mostrandoFallbackManual = false;
    this.mensajeFallbackManual = '';
    this.flagBuscando = true;

    const searchData: SignoSedeSearch = {};

    if (data.rubro) {
      searchData.rubro = data.rubro;
    }
    if (data.especialidad) {
      searchData.especialidad = data.especialidad;
    }
    if (this.planId) {
      searchData.plan = this.planId;
    }
    if (this.modoManualUbicacion) {
      if (this.zonaSeleccionada) {
        searchData.zona = this.zonaSeleccionada;
      }
      if (this.localidadSeleccionada) {
        searchData.localidad = this.localidadSeleccionada as any;
      }
    }

    try {
      const resultados = this.modoManualUbicacion
        ? await this.buscarConFallbackManual(data)
        : await this.cartillaService.getBusquedaNomenclador(searchData);

      if (!this.modoManualUbicacion && this.geoEstado === 'ok') {
        this.filtrarCentrosCercanos(resultados || []);
        this.gruposManuales = [];
      } else if (this.modoManualUbicacion) {
        this.resultadosCentrosCercanos = resultados || [];
        this.centrosCercanosSinFiltro = [...this.resultadosCentrosCercanos];
        this.gruposManuales = this.computarGruposManuales();
      } else {
        this.resultadosCentrosCercanos = [];
        this.resultadosCentrosLejanos = [];
        this.centrosCercanosSinFiltro = [];
        this.centrosLejanosSinFiltro = [];
        this.gruposManuales = [];
      }

      this.sinResultados = this.totalCentrosSinFiltro === 0;
    } catch (error) {
      console.error('Error en la búsqueda:', error);
      this.resultadosCentrosCercanos = [];
      this.resultadosCentrosLejanos = [];
      this.centrosCercanosSinFiltro = [];
      this.centrosLejanosSinFiltro = [];
      this.sinResultados = true;
    }

    this.flagBuscando = false;

    if (window.innerWidth < 768) {
      setTimeout(() => {
        const el = document.getElementById('resultados');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }

  private async buscarConFallbackManual(data: { rubro?: string; especialidad?: string }): Promise<SignoCentroMedico[]> {
    const base: SignoSedeSearch = {};
    if (data.rubro) base.rubro = data.rubro;
    if (data.especialidad) base.especialidad = data.especialidad;
    if (this.planId) base.plan = this.planId;

    if (this.localidadSeleccionada) {
      const r1 = await this.cartillaService.getBusquedaNomenclador({
        ...base,
        zona: this.zonaSeleccionada || undefined,
        localidad: this.localidadSeleccionada as any,
      });
      if (r1.length > 0) return r1;
    }
    if (this.zonaSeleccionada) {
      const r2 = await this.cartillaService.getBusquedaNomenclador({ ...base, zona: this.zonaSeleccionada });
      if (r2.length > 0) {
        if (this.localidadSeleccionada) {
          this.mostrandoFallbackManual = true;
          this.mensajeFallbackManual = 'No hay resultados en la localidad seleccionada. Se muestran los de la zona.';
        }
        return r2;
      }
    }
    if (this.localidadSeleccionada || this.zonaSeleccionada) {
      this.mostrandoFallbackManual = true;
      this.mensajeFallbackManual = 'No hay resultados en la zona seleccionada. Se muestran todos los disponibles.';
    }
    return this.cartillaService.getBusquedaNomenclador(base);
  }

  get modoAgrupadoManual(): 'zona' | 'localidad' | 'ninguno' {
    if (this.mostrandoFallbackManual) return 'zona';
    if (this.zonaSeleccionada && !this.localidadSeleccionada) return 'localidad';
    if (!this.zonaSeleccionada && !this.localidadSeleccionada) return 'zona';
    return 'ninguno';
  }

  private computarGruposManuales(): { titulo: string; centros: SignoCentroMedico[] }[] {
    const modo = this.modoAgrupadoManual;
    const grupos = new Map<string, SignoCentroMedico[]>();
    for (const centro of this.resultadosCentrosCercanos) {
      const clave = modo === 'localidad'
        ? (centro.localidad?.nombre || 'Sin localidad')
        : (centro.zona?.concepto || 'Sin zona');
      if (!grupos.has(clave)) {
        grupos.set(clave, []);
      }
      grupos.get(clave)!.push(centro);
    }
    const prefijo = modo === 'localidad' ? 'Localidad: ' : 'Zona: ';
    return Array.from(grupos.entries()).map(([clave, centros]) => ({
      titulo: prefijo + this.normalizarConcepto(clave),
      centros,
    }));
  }

  private normalizarBusqueda(texto: string): string {
    return (texto || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  }

  private fuzzyCoincide(filtro: string, texto: string): boolean {
    const f = this.normalizarBusqueda(filtro);
    const t = this.normalizarBusqueda(texto);
    if (!f) return true;
    if (t.includes(f)) return true;
    let qi = 0;
    for (let i = 0; i < t.length && qi < f.length; i++) {
      if (t[i] === f[qi]) {
        qi++;
      }
    }
    return qi === f.length;
  }

  private conceptosTexto(items: any): string {
    if (!items) return '';
    if (Array.isArray(items)) {
      return items.map(i => typeof i === 'string' ? i : (i?.concepto || '')).filter(Boolean).join(' ');
    }
    return typeof items === 'string' ? items : (items?.concepto || '');
  }

  private centroCoincideFiltro(centro: SignoCentroMedico, filtro: string): boolean {
    const campos = [
      centro.nombre,
      centro.calle,
      centro.altura,
      centro.zona?.concepto,
      centro.localidad?.nombre,
      this.conceptosTexto(centro.rubros),
      this.conceptosTexto(centro.especialidades),
      centro.horarios,
    ];
    return campos.some(c => this.fuzzyCoincide(filtro, c || ''));
  }

  normalizarTexto(texto: string): string {
    return (texto || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  }

  onFiltroNombreChange(): void {
    const filtro = this.normalizarTexto(this.filtroNombre);
    if (!filtro) {
      this.resultadosCentrosCercanos = [...this.centrosCercanosSinFiltro];
      this.resultadosCentrosLejanos = [...this.centrosLejanosSinFiltro];
    } else {
      this.resultadosCentrosCercanos = this.centrosCercanosSinFiltro.filter(c =>
        this.centroCoincideFiltro(c, this.filtroNombre)
      );
      this.resultadosCentrosLejanos = this.centrosLejanosSinFiltro.filter(c =>
        this.centroCoincideFiltro(c, this.filtroNombre)
      );
    }
    this.gruposManuales = this.computarGruposManuales();
  }

  filtrarCentrosCercanos(centros: SignoCentroMedico[]): void {
    if (!Array.isArray(centros) || centros.length === 0) {
      this.resultadosCentrosCercanos = [];
      this.resultadosCentrosLejanos = [];
      this.centrosCercanosSinFiltro = [];
      this.centrosLejanosSinFiltro = [];
      this.mostrandoCentroMasLejano = false;
      return;
    }

    const conDistancia = centros
      .filter(c => c.latitud != null && c.longitud != null && c.latitud !== 0 && c.longitud !== 0)
      .map(c => {
        const distancia = this.calcularDistancia(
          this.userLocation.latitude,
          this.userLocation.longitude,
          c.latitud,
          c.longitud
        );
        this.distanciaCache.set(c, distancia);
        return { centro: c, distancia };
      })
      .sort((a, b) => a.distancia - b.distancia);

    const cercanos = conDistancia
      .filter(item => item.distancia <= this.DISTANCIA_CERCANA)
      .map(item => item.centro);

    const lejanos = conDistancia
      .filter(item => item.distancia > this.DISTANCIA_CERCANA && item.distancia <= this.DISTANCIA_LEJANA)
      .map(item => item.centro);

    this.fisherYatesShuffle(lejanos);
    const lejanosSeleccionados = lejanos.slice(0, this.MAX_LEJANOS);

    if (cercanos.length === 0 && lejanosSeleccionados.length === 0 && conDistancia.length > 0) {
      const masCercano = conDistancia[0].centro;
      this.resultadosCentrosCercanos = [];
      this.resultadosCentrosLejanos = [masCercano];
      this.mostrandoCentroMasLejano = true;
    } else {
      this.resultadosCentrosCercanos = [...cercanos];
      this.resultadosCentrosLejanos = [...lejanosSeleccionados];
      this.mostrandoCentroMasLejano = false;
    }

    this.centrosCercanosSinFiltro = [...this.resultadosCentrosCercanos];
    this.centrosLejanosSinFiltro = [...this.resultadosCentrosLejanos];

    this.sinResultadosCercania = cercanos.length === 0 && centros.length > 0 && !this.mostrandoCentroMasLejano;
  }

  private fisherYatesShuffle(arr: SignoCentroMedico[]): void {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
  }

  calcularDistancia(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371;
    const dLat = this.deg2rad(lat2 - lat1);
    const dLon = this.deg2rad(lon2 - lon1);
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.deg2rad(lat1)) *
        Math.cos(this.deg2rad(lat2)) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  deg2rad(deg: number): number {
    return deg * (Math.PI / 180);
  }

  getDistanciaCentro(centro: SignoCentroMedico): string {
    const dist = this.distanciaCache.get(centro);
    if (dist === undefined) {
      return '';
    }
    return dist < 1 ? '< 1 km' : Math.round(dist) + ' km';
  }

  getConcepto(obj: any): string {
    if (!obj) return '';
    if (Array.isArray(obj)) {
      return obj.map((o: any) => o?.concepto || '').filter(Boolean).join(', ');
    }
    return obj?.concepto || '';
  }

  openMap(centro: SignoCentroMedico): void {
    this.dialog.open(CentrosCardComponent, {
      width: '80vw',
      maxWidth: '700px',
      maxHeight: '80vh',
      panelClass: 'cartilla-map-dialog',
      data: centro
    });
  }

  telCentro(centro: SignoCentroMedico): void {
    const allNumbers: PhoneNumberInfo[] = [];

    if (centro.telefonos) {
      const phonesArray = centro.telefonos
        .split(',')
        .map(p => p.replace(/[^\d+]/g, '').trim())
        .filter(p => p.length > 0);
      phonesArray.forEach(p => allNumbers.push({ number: p, type: 'phone' }));
    }
    if (centro.celular) {
      const mobilesArray = centro.celular
        .split(',')
        .map(p => p.replace(/[^\d+]/g, '').trim())
        .filter(p => p.length > 0);
      mobilesArray.forEach(m => allNumbers.push({ number: m, type: 'whatsapp' }));
    }

    const uniqueKeys = new Set<string>();
    this.phoneNumbersForModal = [];

    allNumbers.forEach(item => {
      const key = `${item.number}-${item.type}`;
      if (!uniqueKeys.has(key)) {
        uniqueKeys.add(key);
        this.phoneNumbersForModal.push(item);
      }
    });

    this.centerNameForModal = centro.nombre || 'Centro Médico';

    if (this.phoneNumbersForModal.length > 1) {
      this.showPhoneModal = true;
    } else if (this.phoneNumbersForModal.length === 1) {
      this.handlePhoneNumberAction(this.phoneNumbersForModal[0]);
    } else {
      alert('No hay número de contacto disponible para este centro.');
    }
  }

  handlePhoneNumberAction(numberInfo: PhoneNumberInfo): void {
    if (numberInfo.type === 'phone') {
      this.callSelectedNumber(numberInfo.number);
    } else if (numberInfo.type === 'whatsapp') {
      this.openWhatsApp(numberInfo.number);
    }
    this.closePhoneModal();
  }

  callSelectedNumber(phoneNumber: string): void {
    window.open(`tel:${phoneNumber}`, '_system');
  }

  openWhatsApp(phoneNumber: string): void {
    const cleanNumber = phoneNumber.replace(/\+/g, '').replace(/\D/g, '');
    window.open(`https://wa.me/${cleanNumber}`, '_system');
  }

  closePhoneModal(): void {
    this.showPhoneModal = false;
    this.phoneNumbersForModal = [];
    this.centerNameForModal = '';
  }
}
